/**
 * 媒體庫挑選器 — 從既有素材中選檔掛到景點插槽。
 *
 * 只顯示符合插槽格式的素材（PNG／WAV／MP4），避免使用者選了會被後端擋下的檔案
 * 才發現格式不對。庫裡沒有想要的檔案時可就地上傳（見下）。
 *
 * ## 為什麼要有預覽窗格
 *
 * 檔名在這裡幫不上忙：`1_pic1.png`、`1_voice1.wav` 在每個景點資料夾都叫同一個
 * 名字，媒體庫裡有上千筆。光看清單根本分不出哪一個是要的，而選錯要等到發布到
 * 船機上才會發現。
 *
 * 所以右側常駐一個預覽窗格，**語音與影片一定要能播**——那兩種在清單裡只有一個
 * 圖示，是最需要預覽的兩種，偏偏也是原本完全看不到內容的兩種。
 *
 * 預覽只載「目前指定的那一個」。清單那邊維持圖片才載縮圖、影音只給圖示，因為
 * 那是一整頁一起載，代價完全不同。
 *
 * ## 點哪裡做什麼
 *
 * - 點列（含檔名）：勾選／取消，同時把預覽切到這一筆——照著挑的順手路徑就會邊選邊看。
 * - 點縮圖：**只預覽不改選取**。多選插槽裡想回頭看已選的那張時，不必先取消再選回來。
 *
 * ## 庫裡沒有的檔可以就地上傳
 *
 * 呼叫端給了 `upload` 就會出現上傳區（見 {@link MediaPickerUpload}）。傳完的素材
 * 會插在清單最前面並自動選取（單選插槽直接取代原本的選取）——它剛上傳，本來就是
 * 使用者要的那一個，還要他再找一次只是在考驗記憶力。景點編輯的每一個插槽（含推薦
 * 點的兩張圖）都給 `upload`；不給的呼叫端維持純挑選行為。
 *
 * ## 景點只看得到自己的資料夾
 *
 * 呼叫端給了 `restrictToFolder`（景點編輯一律給窗屏編號），清單與資料夾下拉就只
 * 剩那個景點底下的內容。窗屏檔案樹每個景點的檔名都一模一樣（`1_pic1.png`、
 * `1_voice1.wav`…），跨景點一起列出來時，光看檔名根本分不出是哪一個——而選錯要
 * 等船靠港、乘客站在玻璃前才看得出來。
 *
 * 限定之後若這個景點的資料夾裡沒有想要的檔，正確的做法是用上傳區傳進來（會自動
 * 歸到該景點的資料夾），而不是去借別的景點的檔：同一份內容重複上傳不會多佔儲存
 * 空間（內容定址去重），但借來的檔會讓兩個景點共用同一列，其中一邊改標籤另一邊
 * 就會莫名其妙掃不到。
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { Button } from './Button';
import { MediaPickerUpload, type PickerUploadTarget } from './MediaPickerUpload';
import { MediaInlinePreview, MediaThumb } from './MediaPreview';
import { listMedia, listMediaTags, type MediaItem, type MediaType } from '../lib/api-client';
import { buildMediaTree, type FolderNode } from '../lib/media-tree';

/** 一次抓的上限。超過就要求使用者用資料夾或檔名縮小範圍，不做分頁。 */
const PAGE_LIMIT = 100;

interface Props {
  open: boolean;
  title: string;
  /** 僅列出此型別的素材 */
  mediaType: MediaType;
  /** 進一步限制 MIME（規格要求 PNG / WAV / MP4） */
  acceptMimes?: readonly string[];
  multiple: boolean;
  /** 已選取的 media id（重開時預選） */
  initialSelected?: readonly string[];
  /** 給了就顯示上傳區；語系與標籤由呼叫端依插槽算好。 */
  upload?: PickerUploadTarget;
  /**
   * 只在這個資料夾（含子資料夾）裡挑，例如景點的窗屏編號 `0`。
   *
   * 省略＝整個媒體庫，僅用於與景點無關的挑選情境。
   */
  restrictToFolder?: string;
  onConfirm: (mediaIds: string[]) => void;
  onClose: () => void;
}

/** 資料夾樹壓平成下拉選單的選項；父節點排在子節點之前。 */
function flattenFolders(nodes: readonly FolderNode[]): FolderNode[] {
  return nodes.flatMap((node) => [node, ...flattenFolders(node.children)]);
}

/** 素材所屬的虛擬資料夾（`assets/3/zh` 這種帶斜線的標籤），用來在清單上分辨同名檔。 */
function folderOf(item: MediaItem): string | null {
  return item.tags.find((t) => t.includes('/')) ?? null;
}

export function MediaPicker({
  open,
  title,
  mediaType,
  acceptMimes,
  multiple,
  initialSelected,
  upload,
  restrictToFolder,
  onConfirm,
  onClose,
}: Props): JSX.Element | null {
  const [selected, setSelected] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [folder, setFolder] = useState('');
  /** 預覽指向的那一筆。與勾選分開——看跟選是兩回事。 */
  const [focused, setFocused] = useState<MediaItem | null>(null);
  /**
   * 這次開啟期間剛上傳的素材。
   *
   * 不重抓清單而是直接插在最前面：剛傳完的檔多半不符合當下的資料夾／檔名篩選
   * 條件，重抓反而會讓它消失。這份只活在挑選器開啟期間。
   */
  const [uploaded, setUploaded] = useState<MediaItem[]>([]);

  /*
   * 依 id 內容而非陣列身分重置：呼叫端每次 render 都會現算一個新陣列，直接放進
   * 相依陣列會讓上層任何一次重繪都把使用者選到一半的結果清掉。
   */
  const initialKey = (initialSelected ?? []).join(',');
  useEffect(() => {
    if (!open) return;
    setSelected(initialKey ? initialKey.split(',') : []);
    setFocused(null);
    setQ('');
    setFolder('');
    setUploaded([]);
  }, [open, initialKey]);

  /**
   * 剛上傳完就選取並預覽。
   *
   * 去重命中（庫裡已有同一份檔）也走這裡：對使用者而言結果一樣是「這個檔可以用
   * 了」，差別只在有沒有真的多存一份物件，那是後端的事。
   */
  const handleUploaded = useCallback(
    (item: MediaItem): void => {
      setUploaded((prev) => (prev.some((u) => u.id === item.id) ? prev : [item, ...prev]));
      setFocused(item);
      setSelected((prev) => {
        if (prev.includes(item.id)) return prev;
        return multiple ? [...prev, item.id] : [item.id];
      });
    },
    [multiple],
  );

  const tagsQuery = useQuery({
    queryKey: ['media.picker.tags'],
    queryFn: () => listMediaTags(),
    enabled: open,
  });

  /** 限定的根資料夾；空字串＝不限定。 */
  const root = restrictToFolder?.trim() ?? '';

  const query = useQuery({
    // root 必須進 key：同一個挑選器元件會被不同景點重用，少了它會拿到上一個景點的快取
    queryKey: ['media.picker', mediaType, q, folder, root],
    queryFn: () =>
      listMedia({
        media_type: mediaType,
        q: q || undefined,
        // 下拉選了子資料夾就用它，否則退回整個景點的資料夾
        tag_prefix: folder || root || undefined,
        limit: PAGE_LIMIT,
      }),
    enabled: open,
  });

  if (!open) return null;

  const fetched = (query.data?.items ?? []).filter(
    (m) => !acceptMimes || acceptMimes.includes(m.mime_type),
  );
  // 剛上傳的排最前面；它可能也在查詢結果裡（去重命中既有素材），故要去掉重複
  const items: MediaItem[] = [
    ...uploaded,
    ...fetched.filter((m) => !uploaded.some((u) => u.id === m.id)),
  ];
  const total = query.data?.total ?? 0;
  const allFolders = flattenFolders(buildMediaTree(tagsQuery.data?.tags ?? []).folders);
  // 限定時連下拉都只留自己的子資料夾——列出別人的路徑等於在邀請人去選別的景點的檔
  const folders = root
    ? allFolders.filter((n) => n.path === root || n.path.startsWith(`${root}/`))
    : allFolders;

  const toggle = (m: MediaItem): void => {
    setFocused(m);
    setSelected((prev) => {
      if (prev.includes(m.id)) return prev.filter((x) => x !== m.id);
      return multiple ? [...prev, m.id] : [m.id];
    });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: 16,
          width: 1020,
          maxWidth: '94vw',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            <strong style={{ fontSize: 14 }}>{title}</strong>
            {/* 限定範圍要講出來：找不到某個檔時，第一個該想到的是「它不在這個景點的資料夾裡」 */}
            {root && (
              <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
                只顯示此景點的資料夾 <code>{root}/…</code>
              </span>
            )}
          </div>
          <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
            {multiple ? `已選 ${selected.length} 個（順序即掛載順序）` : '單選'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            style={{ padding: 8, maxWidth: 260 }}
            title="以媒體庫資料夾縮小範圍"
          >
            <option value="">{root ? `${root} 底下全部` : '全部資料夾'}</option>
            {/* 用完整路徑而非末段：每個景點底下都有一個 zh，只寫末段等於沒寫 */}
            {folders.map((node) => (
              <option key={node.path} value={node.path}>
                {`${node.path}（${node.total}）`}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="以檔名搜尋…"
            style={{ padding: 8, flex: 1 }}
          />
        </div>

        {upload && (
          <MediaPickerUpload
            {...upload}
            {...(acceptMimes ? { acceptMimes } : {})}
            onUploaded={handleUploaded}
          />
        )}

        <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0 }}>
          <div
            style={{
              overflow: 'auto',
              flex: 1,
              minWidth: 0,
              border: '1px solid #e5e7eb',
              borderRadius: 6,
            }}
          >
            {query.isLoading && <div style={{ padding: 16 }}>載入中…</div>}
            {!query.isLoading && items.length === 0 && (
              <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>
                {folder || q
                  ? '這個範圍內沒有符合格式的素材。試著清掉資料夾或搜尋條件。'
                  : `${
                      root
                        ? `此景點的資料夾 ${root}/ 底下沒有符合格式的素材`
                        : '媒體庫沒有符合格式的素材'
                    }（需要 ${acceptMimes?.join('、') ?? mediaType}）。${
                      upload
                        ? '直接用上方的上傳區傳一個——會自動歸到這個景點的資料夾。'
                        : '請先到「媒體庫」上傳。'
                    }`}
              </div>
            )}
            {items.map((m) => {
              const idx = selected.indexOf(m.id);
              const picked = idx >= 0;
              const isFocused = focused?.id === m.id;
              const dir = folderOf(m);
              return (
                <div
                  key={m.id}
                  onClick={() => toggle(m)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: 8,
                    cursor: 'pointer',
                    background: picked ? '#eff6ff' : isFocused ? '#f9fafb' : 'transparent',
                    borderLeft: `3px solid ${isFocused ? '#2563eb' : 'transparent'}`,
                    borderBottom: '1px solid #f3f4f6',
                  }}
                >
                  <input type="checkbox" checked={picked} readOnly />
                  {/* 只預覽不改選取——多選時想回頭確認已選的那張，不必先取消 */}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setFocused(m);
                    }}
                    title="預覽"
                    style={{ display: 'flex', flexShrink: 0 }}
                  >
                    <MediaThumb item={m} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, wordBreak: 'break-all' }}>{m.filename}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                      {/* 同名檔滿地都是，資料夾才是分辨的依據，放在最前面 */}
                      {dir ? `${dir} · ` : ''}
                      {(Number(m.size_bytes) / 1024 / 1024).toFixed(2)} MB
                      {m.language ? ` · ${m.language}` : ''}
                    </div>
                  </div>
                  {multiple && picked && (
                    <span style={{ fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                      #{idx + 1}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div
            style={{
              width: 360,
              flexShrink: 0,
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              padding: 12,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {!focused ? (
              <div style={{ margin: 'auto', color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>
                點左側任一筆即可在此預覽
                <br />
                {mediaType === 'audio'
                  ? '（語音可直接試聽）'
                  : mediaType === 'video'
                    ? '（影片可直接播放）'
                    : ''}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>
                  {focused.filename}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 160,
                    background: '#f9fafb',
                    borderRadius: 4,
                    padding: 8,
                  }}
                >
                  <MediaInlinePreview item={focused} maxHeight="42vh" autoPlayAudio />
                </div>
                <dl
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: '#374151',
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: '4px 10px',
                  }}
                >
                  <dt>資料夾</dt>
                  <dd style={{ margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {folderOf(focused) ?? '—'}
                  </dd>
                  <dt>MIME</dt>
                  <dd style={{ margin: 0, fontFamily: 'monospace' }}>{focused.mime_type}</dd>
                  <dt>大小</dt>
                  <dd style={{ margin: 0 }}>
                    {(Number(focused.size_bytes) / 1024 / 1024).toFixed(2)} MB
                  </dd>
                  <dt>語系</dt>
                  <dd style={{ margin: 0 }}>{focused.language ?? '—'}</dd>
                </dl>
                <Button variant="secondary" onClick={() => toggle(focused)}>
                  {selected.includes(focused.id) ? '取消選取這一筆' : '選取這一筆'}
                </Button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* 只抓前 100 筆，剩下的不會出現在清單上——不講的話使用者會以為檔案不見了 */}
          <span style={{ fontSize: 11, color: '#9ca3af' }}>
            {total > PAGE_LIMIT
              ? `符合條件共 ${total} 筆，僅顯示前 ${PAGE_LIMIT} 筆；請用資料夾或檔名縮小範圍`
              : items.length > 0
                ? `共 ${items.length} 筆`
                : ''}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>
              取消
            </Button>
            <Button variant="primary" onClick={() => onConfirm(selected)}>
              確定掛載
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
